import React, { Component, Fragment } from 'react';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import CurrencySelect from '~/CurrencySelect';
import { ExportType as RoMaxPriceExportType } from '~/RoMaxPrice';
import ActivityIndicator from '~/UI/ActivityIndicator';
import Button, { Link } from '~/UI/Button';
import linkButtonStyles from '~/UI/Button/link-small.less';
import primaryIconButtonStyles from '~/UI/Button/primary-icon-full.less';
import Icon from '~/UI/Icon/Icon';
import Popover from '~/UI/Popover';
import ProductGroupDownloadButton from './ProductGroupDownloadButton';

import styles from './styles.less';

export interface Props {
	referenceRect: ClientRect | null;
	group: UserProductGroup;
	currency: string | null;
	canExportEvidence: boolean;
	isFetching: boolean;
	onProductsExport(a: string | null): void;
	onEvidenceExport(): Promise<void>;
	onCzMaxPriceExport(): void;
	onCzReimbursementExport(): void;
	onSkMaxPriceCNRefExport(): void;
	onSkMaxPriceCNRefPzluExport(): void;
	onSkMaxPriceRawExport(): void;
	onAeMaxPriceExport(): void;
	onSiMaxPriceExport(): void;
	onHrMaxPriceExport(): void;
	onClose(event: MouseEvent): void;
}

interface State {
	currency: string | null;
}

export default class DownloadPopover extends Component<Props, State> {
	static defaultProps = {
		referenceRect: null,
	};

	state = {
		currency: this.props.currency || 'EUR',
	};

	render() {
		const { referenceRect, isFetching, onClose } = this.props;

		if (!referenceRect) {
			return null;
		}

		return (
			<Popover referenceRect={referenceRect} width={240} onClose={onClose}>
				<p className={styles.perex}>
					Choose export:{' '}
					<Link styles={linkButtonStyles} to='/help#export'>
						export info
					</Link>
				</p>
				<p className={styles.subPerex}>Currency conversion rates settings from list details are used in the export.</p>
				{isFetching ? <ActivityIndicator /> : this.renderDownloads()}
			</Popover>
		);
	}

	renderDownloads() {
		const {
			group,
			canExportEvidence,
			onProductsExport,
			onEvidenceExport,
			onCzMaxPriceExport,
			onCzReimbursementExport,
			onSkMaxPriceCNRefExport,
			onSkMaxPriceCNRefPzluExport,
			onSkMaxPriceRawExport,
			onAeMaxPriceExport,
			onSiMaxPriceExport,
			onHrMaxPriceExport,
		} = this.props;

		return (
			<Fragment>
				{group.hasCzMaxPriceExportLink() ? (
					<div className={styles.row}>
						<Button styles={primaryIconButtonStyles} onClick={onCzMaxPriceExport}>
							<Icon name='download'>download</Icon> CZ Max Price
						</Button>
					</div>
				) : null}

				{group.hasCzReimbursementExportLink() ? (
					<div className={styles.row}>
						<Button styles={primaryIconButtonStyles} onClick={onCzReimbursementExport}>
							<Icon name='download'>download</Icon> CZ Reimbursement
						</Button>
					</div>
				) : null}

				{group.hasSkMaxPriceCNRefExportLink() ? (
					<div className={styles.row}>
						<Button styles={primaryIconButtonStyles} onClick={onSkMaxPriceCNRefExport}>
							<Icon name='download'>download</Icon> SK Max Price - CNRef LP
						</Button>
					</div>
				) : null}

				{group.hasSkMaxPriceCNRefPzluExportLink() ? (
					<div className={styles.row}>
						<Button styles={primaryIconButtonStyles} onClick={onSkMaxPriceCNRefPzluExport}>
							<Icon name='download'>download</Icon> SK Max Price - CNRef DP
						</Button>
					</div>
				) : null}

				{group.hasSkMaxPriceRawExportLink() ? (
					<div className={styles.row}>
						<Button styles={primaryIconButtonStyles} onClick={onSkMaxPriceRawExport}>
							<Icon name='download'>download</Icon> SK Max Price - raw
						</Button>
					</div>
				) : null}

				{group.hasAeMaxPriceExportLink() ? (
					<div className={styles.row}>
						<Button styles={primaryIconButtonStyles} onClick={onAeMaxPriceExport}>
							<Icon name='download'>download</Icon> AE Max Price
						</Button>
					</div>
				) : null}

				{group.hasSiMaxPriceExportLink() ? (
					<div className={styles.row}>
						<Button styles={primaryIconButtonStyles} onClick={onSiMaxPriceExport}>
							<Icon name='download'>download</Icon> SI Max Price: B1
						</Button>
					</div>
				) : null}

				{group.hasHrMaxPriceExportLink() ? (
					<div className={styles.row}>
						<Button styles={primaryIconButtonStyles} onClick={onHrMaxPriceExport}>
							<Icon name='download'>download</Icon> HR Max Price
						</Button>
					</div>
				) : null}

				{group.hasRoMaxPriceExportLink() ? (
					<>
						<p className={styles.row}>
							<Link to='/profile/settings/ro-max-price'>Setup RO Max Price Export</Link>
						</p>

						<div className={styles.row}>
							<ProductGroupDownloadButton
								exportType={RoMaxPriceExportType.annex1Xls}
								group={group}
								title='RO Max Price: Annex 1 (XLS)'
							/>
						</div>
						<div className={styles.row}>
							<ProductGroupDownloadButton
								exportType={RoMaxPriceExportType.annex1Pdf}
								group={group}
								title='RO Max Price: Annex 1 (PDF)'
							/>
						</div>
						<div className={styles.row}>
							<ProductGroupDownloadButton
								exportType={RoMaxPriceExportType.machetaXls}
								group={group}
								title='RO Max Price: Macheta (XLS)'
							/>
						</div>
						<div className={styles.row}>
							<ProductGroupDownloadButton
								exportType={RoMaxPriceExportType.recipeData}
								group={group}
								title='RO Max Price: Import Recipe Data (XLS)'
							/>
						</div>
					</>
				) : null}

				{canExportEvidence ? (
					<div className={styles.row}>
						<Button styles={primaryIconButtonStyles} onClick={onEvidenceExport}>
							<Icon name='download'>download</Icon> Evidence
						</Button>
					</div>
				) : null}

				{this.renderCurrencySelect()}

				<div className={styles.row}>
					<Button styles={primaryIconButtonStyles} onClick={() => onProductsExport(this.state.currency)}>
						<Icon name='download'>download</Icon> Products data
					</Button>
				</div>
			</Fragment>
		);
	}

	renderCurrencySelect() {
		const { currency } = this.state;

		return (
			<div className={styles.row}>
				<p className={styles.line}>
					<span className={styles.lineTitle}>or</span>
				</p>
				<p className={styles.label}>Currency</p>
				<CurrencySelect
					excludeSourcePrice
					currentCurrency={currency}
					id='download-popover-currency-select'
					onChange={(currency) => this.setState({ currency })}
				/>
			</div>
		);
	}
}
